/**
 * Composable для гибридного диалога (scripted + AI).
 * Оборачивает useAiChat, добавляя машину состояний сценария.
 * См. спецификацию: .ai/specs/hybrid-chat.md
 */

import { useAiChat, type ChatMessage } from 'src/composables/useAiChat';
import { getDialogStep } from 'src/data/dialog-script';
import { isPhoneValid, isEmailValid } from 'src/composables/utils/validators';
import type {
  DialogStepId,
  DialogMeta,
  DialogContext,
  LeadSignal,
  VisitState,
  CollectedContacts,
  ChatChip,
} from './types';

/**
 * Порог тишины: минимум сообщений в инфо-фазе до предложения контакта.
 */
const LEAD_SILENCE_THRESHOLD = 4;

/**
 * Информационные шаги (для подсчёта infoPhaseCount).
 */
const INFO_PHASE_STEPS: DialogStepId[] = ['about', 'projects', 'services', 'ai-usage', 'prices'];

const META_FENCE = '```meta';

/**
 * Отрезает meta-блок от видимого контента, включая незавершённый:
 * пока стрим идёт или если ответ оборвался по max_tokens.
 */
const stripMetaBlock = (content: string): string => {
  const start = content.indexOf(META_FENCE);
  if (start === -1) return content;
  const closing = content.indexOf('```', start + META_FENCE.length);
  if (closing === -1) return content.slice(0, start).trimEnd();
  return `${content.slice(0, start)}${content.slice(closing + 3)}`.trim();
};

export const useHybridChat = () => {
  const { t } = useI18n();
  const route = useRoute();
  const formDataService = useFeathersService('form-data');

  // Базовый AI-чат (стриминг, история, ошибки)
  const aiChat = useAiChat();

  // AbortController для отмены fetch при stopStreaming
  let abortController: AbortController | null = null;

  // === Состояние диалога ===
  const currentStep = ref<DialogStepId>('greeting');
  const visitState = ref<VisitState>({ messageCount: 0, infoPhaseCount: 0 });
  const leadSignals = ref<LeadSignal[]>([]);
  const leadDeclined = ref(false);
  const contacts = ref<CollectedContacts>({});

  // === Вычисляемые свойства ===

  /**
   * Текущий шаг сценария (данные).
   */
  const currentDialogStep = computed(() => getDialogStep(currentStep.value));

  /**
   * Чипы для текущего шага.
   */
  const currentChips = computed<ChatChip[]>(() => {
    return currentDialogStep.value?.chips || [];
  });

  /**
   * Нужно ли показывать поле ввода контакта.
   */
  const isCollectingContact = computed(() => {
    return !!currentDialogStep.value?.collect;
  });

  /**
   * Текст скриптового шага (локализованный).
   */
  const scriptStepText = computed(() => {
    const step = currentDialogStep.value;
    if (!step || step.mode !== 'script' || !step.textKey) return '';
    return t(step.textKey);
  });

  // === Переходы ===

  /**
   * Переход к указанному шагу.
   */
  const goTo = (stepId: DialogStepId) => {
    const step = getDialogStep(stepId);
    if (!step) {
      console.warn(`[useHybridChat] Step "${stepId}" not found`);
      return;
    }

    currentStep.value = stepId;

    // Счётчики обновляются в handleUserMessage, а не здесь

    // Если шаг скриптовый — добавляем сообщение ассистента
    if (step.mode === 'script' && step.textKey) {
      const text = t(step.textKey);
      aiChat.messages.value.push({ role: 'assistant', content: text });
    }

    // Обработка "Не сейчас" (отказ от контакта)
    if (stepId === 'farewell' && !leadDeclined.value && leadSignals.value.length > 0) {
      // Если пользователь дошёл до farewell после лид-сигналов, но не оставил контакт
      // — считаем это отказом
      leadDeclined.value = true;
    }
  };

  /**
   * Обработка сообщения пользователя (свободный текст).
   */
  const handleUserMessage = async (text: string) => {
    if (!text.trim() || aiChat.isStreaming.value) return;

    const step = currentDialogStep.value;
    if (!step) return;

    // Если шаг сбора контакта — валидируем и сохраняем
    if (step.collect) {
      const isValid = step.collect === 'phone'
        ? isPhoneValid(text.trim())
        : isEmailValid(text.trim());

      if (!isValid) {
        // Дружелюбная реплика об ошибке
        const errorKey = step.collect === 'phone'
          ? 'HYBRID_CHAT.ERROR.INVALID_PHONE'
          : 'HYBRID_CHAT.ERROR.INVALID_EMAIL';
        aiChat.messages.value.push({ role: 'assistant', content: t(errorKey) });
        return;
      }

      // Сохраняем контакт
      if (step.collect === 'phone') {
        contacts.value.phone = text.trim();
      } else {
        contacts.value.email = text.trim();
      }

      const confirmKey = step.collect === 'phone'
        ? 'HYBRID_CHAT.CONFIRM.PHONE_SAVED'
        : 'HYBRID_CHAT.CONFIRM.EMAIL_SAVED';

      // Сохраняем лид и только после успеха подтверждаем клиенту
      const saved = await saveLead();
      if (!saved) {
        aiChat.messages.value.push({ role: 'assistant', content: t('HYBRID_CHAT.SAVE_FAILED') });
        return;
      }
      aiChat.messages.value.push({ role: 'assistant', content: t(confirmKey) });
      goTo('lead-done');
      return;
    }

    // Если шаг в режиме 'script' (не сбор контакта) — тоже отправляем на сервер:
    // AI классифицирует свободный текст и рекомендует следующий шаг.
    if (step.mode === 'script') {
      await sendToServer(text);
      return;
    }

    // Если шаг в режиме 'ai' — отправляем на сервер с dialogContext
    if (step.mode === 'ai') {
      // Обновляем счётчики (считаем только реальные сообщения пользователя)
      visitState.value.messageCount++;
      if (INFO_PHASE_STEPS.includes(currentStep.value)) {
        visitState.value.infoPhaseCount++;
      }
      await sendToServer(text);
    }
  };

  /**
   * Отправка сообщения на сервер с контекстом диалога.
   */
  const sendToServer = async (text: string) => {
    // Формируем dialogContext
    const dialogContext: DialogContext = {
      step: currentStep.value,
      visitState: visitState.value,
      leadSignals: leadSignals.value,
      leadDeclined: leadDeclined.value,
    };

    // Снимаем историю ДО добавления текущего сообщения,
    // т.к. сервер сам добавляет body.message в конец контекста
    const historySnapshot = aiChat.messages.value.filter((m) => m.role !== 'system');

    // Добавляем сообщение пользователя
    aiChat.messages.value.push({ role: 'user', content: text.trim() });
    // Счётчики уже обновлены в handleUserMessage

    // Добавляем placeholder для ответа
    aiChat.messages.value.push({ role: 'assistant', content: '' });

    aiChat.error.value = null;
    aiChat.isStreaming.value = true;
    abortController = new AbortController();

    const title = typeof document !== 'undefined' && document.title ? document.title : undefined;
    const pageContext = title ? { path: route.path, title } : { path: route.path };

    try {
      const response = await fetch(`${process.env.API_URL as string}/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: historySnapshot,
          pageContext,
          dialogContext,
        }),
        signal: abortController.signal,
      });

      if (response.status === 429) {
        aiChat.error.value = 'RATE_LIMITED';
        aiChat.messages.value.pop();
        aiChat.isStreaming.value = false;
        return;
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      const assistantIndex = aiChat.messages.value.length - 1;
      let buffer = '';
      let rawContent = '';
      let meta: DialogMeta | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              const message = aiChat.messages.value[assistantIndex];
              if (!message) {
                aiChat.stopStreaming();
                return;
              }
              // Очищаем из сырого потока: в накопленном видимом тексте
              // открывающий фенс уже потерян, и хвост meta просочился бы
              rawContent += parsed.content;
              message.content = stripMetaBlock(rawContent);
            }
            if (parsed.meta) {
              meta = parsed.meta as DialogMeta;
            }
            if (parsed.error) {
              aiChat.error.value = typeof parsed.error === 'string' ? parsed.error : 'ERROR';
            }
          } catch {
            // Игнорируем невалидный JSON
          }
        }
      }

      // Обработка метаданных от AI
      if (meta) {
        processMeta(meta);
      }

      // Финальная нормализация видимого контента
      if (aiChat.messages.value[assistantIndex]) {
        aiChat.messages.value[assistantIndex].content =
          stripMetaBlock(rawContent).trim();
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Отменено — ничего не делаем
      } else {
        console.error('[useHybridChat] Error:', err);
        aiChat.error.value = 'ERROR';
        const lastMsg = aiChat.messages.value[aiChat.messages.value.length - 1];
        if (lastMsg?.role === 'assistant' && !lastMsg.content) {
          aiChat.messages.value.pop();
        }
      }
    } finally {
      aiChat.isStreaming.value = false;
      abortController = null;
    }
  };

  /**
   * Сохранение собранного лида через form-data сервис.
   * Подстраиваемся под существующую схему form-data: name/section/phone обязательны.
   */
  const saveLead = async (): Promise<boolean> => {
    if (!contacts.value.phone && !contacts.value.email) return false;

    try {
      const formData = formDataService.new({
        type: 'ai-chat-lead',
        title: 'Лид из AI-чата',
        section: 'ai-chat',
        name: '',
        phone: contacts.value.phone || '',
        ...(contacts.value.email ? { email: contacts.value.email } : {}),
        data: { source: 'ai-chat' },
      });
      await formData.save();
      return true;
    } catch (err) {
      console.error('[useHybridChat] Failed to save lead:', err);
      return false;
    }
  };

  /**
   * Обработка метаданных от AI (классификация намерения, лид-сигналы).
   */
  const processMeta = (meta: DialogMeta) => {
    // Добавляем лид-сигнал если есть
    if (meta.leadSignal && !leadSignals.value.includes(meta.leadSignal)) {
      leadSignals.value.push(meta.leadSignal);
    }

    // Определяем, нужно ли переходить к следующему шагу
    if (meta.nextStep) {
      // Проверка: не предлагаем контакт если не прошли порог тишины
      if (
        (meta.nextStep === 'lead-offer' || meta.nextStep === 'collect-phone' || meta.nextStep === 'collect-email') &&
        visitState.value.infoPhaseCount < LEAD_SILENCE_THRESHOLD
      ) {
        // Ещё рано — остаёмся на текущем шаге или идём в info-шаг
        return;
      }

      // Проверка: не предлагаем контакт если уже отклонили
      if (
        (meta.nextStep === 'lead-offer' || meta.nextStep === 'collect-phone' || meta.nextStep === 'collect-email') &&
        leadDeclined.value
      ) {
        return;
      }

      // Переходим к рекомендованному шагу
      goTo(meta.nextStep);
    }
  };

  /**
   * Обработка клика по чипу.
   */
  const handleChipClick = (chip: ChatChip) => {
    const target = getDialogStep(chip.target);

    goTo(chip.target);

    // Целевой шаг в AI-режиме: отправляем подпись чипа как сообщение
    // пользователя — иначе после перехода не будет ни ответа, ни запроса
    // к серверу (шаги about/projects/services/ai-usage/prices).
    if (target?.mode === 'ai') {
      void handleUserMessage(t(chip.labelKey));
    }
  };

  /**
   * Пропуск сбора контакта (кнопка "Пропустить").
   */
  const skipContact = () => {
    leadDeclined.value = true;
    goTo('lead-done');
  };

  /**
   * Очистка диалога (сброс состояния).
   */
  const reset = () => {
    // Прерываем именно гибридный fetch: aiChat.clearHistory()
    // абортит собственный controller, который в hybrid-режиме не используется
    stopStreaming();
    aiChat.clearHistory();
    currentStep.value = 'greeting';
    visitState.value = { messageCount: 0, infoPhaseCount: 0 };
    leadSignals.value = [];
    leadDeclined.value = false;
    contacts.value = {};
    // Показываем приветствие заново
    goTo('greeting');
  };

  // Инициализация: показываем greeting при первом вызове
  if (currentStep.value === 'greeting' && aiChat.messages.value.length === 0) {
    goTo('greeting');
  }

  /**
   * Остановка текущего стрима.
   */
  const stopStreaming = () => {
    abortController?.abort();
    aiChat.isStreaming.value = false;
  };

  return {
    // Из useAiChat
    messages: aiChat.messages,
    isStreaming: aiChat.isStreaming,
    error: aiChat.error,
    stopStreaming,

    // Состояние диалога
    currentStep,
    currentDialogStep,
    currentChips,
    isCollectingContact,
    scriptStepText,
    contacts,
    leadSignals,
    leadDeclined,

    // Действия
    goTo,
    handleUserMessage,
    handleChipClick,
    skipContact,
    reset,
  };
};
